/**
 * The words for the people using a site, and for whether it has sign-in at all.
 *
 * COPIES of the pure half of src/webapp/appUsers.ts — the phone's People tab — so an owner who
 * uses both reads the same sentences. Copied rather than imported for the reason api/owner.ts
 * gives: that module fetches its token from CognitoService, which reaches expo-secure-store and
 * so React Native, and importing one function from it would pull that into the browser bundle.
 * Change the two together; better, move the phone's pure half into an import-free module both
 * clients import.
 */
import type { AppPlan } from '@/webapp/plan/planTypes';
import type { AppUser } from '../api/owner';
import type { SiteCheck } from './siteStatus';

/** "3 days ago" — an exact timestamp is noise when the question is "recently?". */
export function lastSeen(user: AppUser, now: Date = new Date()): string {
  const when = user.lastActiveAt ?? user.joinedAt;
  if (!when) return 'never';
  const days = Math.floor((now.getTime() - new Date(when).getTime()) / 86_400_000);
  if (!Number.isFinite(days) || days < 0) return 'just now';
  if (days === 0) return 'today';
  if (days === 1) return 'yesterday';
  if (days < 30) return `${days} days ago`;
  const months = Math.floor(days / 30);
  return months === 1 ? 'a month ago' : `${months} months ago`;
}

/** What Block does, in the words the confirmation shows. The server's rule, and no more. */
export const BLOCK_EXPLAINER =
  "While they are blocked, their account can't save or load anything on your site, or use its AI. "
  + 'What they saved is kept, they can still open the site like any visitor, and you can let them back in at any time.';

const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

/** "Joined Sep 3", with the year only when it is not this one. Null when nobody recorded it. */
export function joinedOn(user: AppUser, now: Date = new Date()): string | null {
  if (!user.joinedAt) return null;
  const when = new Date(user.joinedAt);
  if (!Number.isFinite(when.getTime())) return null;
  const day = `${MONTHS[when.getMonth()]} ${when.getDate()}`;
  return when.getFullYear() === now.getFullYear() ? `Joined ${day}` : `Joined ${day}, ${when.getFullYear()}`;
}

/** The line under a person's email: blocked or not, when they joined, when they were last seen. */
export function personLine(user: AppUser, now: Date = new Date()): string {
  const blocked = user.role === 'blocked';
  return [
    blocked ? 'Blocked' : null,
    joinedOn(user, now),
    blocked ? null : `Last seen ${lastSeen(user, now)}`,
  ].filter(Boolean).join(' · ');
}

/**
 * Whether a site asks people to sign in, read off its code. There is no stored flag: the
 * generated code IS the setting, and a chat edit that adds or removes sign-in changes it.
 */
export type Accounts = 'none' | 'on-save' | 'on-ask';

export function accountsIn(source: string | undefined): Accounts {
  const code = source ?? '';
  if (code.includes('S.cloud(')) return 'on-save';
  if (code.includes('S.auth')) return 'on-ask';
  return 'none';
}

/** Who can open the published page, as the server says. */
export type SiteReach = 'public' | 'password' | 'offline';

/** The server's answer, in the phone's terms. Null while there is none: say nothing about access. */
export function reachOf(site: SiteCheck): SiteReach | null {
  if (site.kind !== 'known') return null;
  return site.status === 'live' ? 'public' : site.status;
}

export const LISTED_BELOW = 'Everyone who has signed in is listed below.';

/**
 * The sign-in status, in plain words. Who can OPEN the site is said only when the server
 * answered (`reach`); with no answer the sentence leaves that part out rather than guess.
 *
 * `listed` is the last sentence, which says where the people are: "below" on the People screen,
 * as on the phone's People tab; elsewhere, where they are instead — or nothing, where there is
 * nowhere to see them yet.
 */
export function signInSummary(
  accounts: Accounts,
  reach: SiteReach | null,
  listed: string = LISTED_BELOW,
): { headline: string; detail: string } {
  if (accounts === 'none') {
    return {
      headline: 'This site has no sign-in.',
      detail: 'Visitors use it without an account, and nothing they do follows them to another phone.',
    };
  }
  const when = accounts === 'on-save' ? 'the first time they save something' : 'when the site asks them to';
  const detail = (
    reach === 'public'
      ? `Anyone with the link can open your site; people sign in ${when}. ${listed}`
      : reach === 'password'
        ? `Only people with the password can open your site; they sign in ${when}. ${listed}`
        : reach === 'offline'
          ? `Your site is offline, so nobody can open it right now. People sign in ${when}. ${listed}`
          : `People sign in ${when}. ${listed}`
  ).trim();
  return { headline: 'Sign-in is on.', detail };
}

/**
 * What to ask for in Chat to add sign-in, naming the thing people would keep: "Add sign-in" on
 * its own gets a login screen bolted on and nothing saved to it.
 */
export function addSignInRequest(plan: AppPlan | null | undefined, source: string | undefined): string {
  return `Let people sign in so their ${keptNoun(plan, source ?? '')} are saved to their account.`;
}

function keptNoun(plan: AppPlan | null | undefined, source: string): string {
  const candidates = [plan?.keeps?.collection, plan?.keepsOff?.collection];
  const re = /S\.store\(\s*['"`]([A-Za-z][\w-]*)['"`]/g;
  let m: RegExpExecArray | null;
  while ((m = re.exec(source)) !== null) candidates.push(m[1]);
  // Only a word that reads as a plural fits "their ___ are saved".
  for (const name of candidates) {
    if (!name) continue;
    const words = name.replace(/([a-z])([A-Z])/g, '$1 $2').replace(/[_-]+/g, ' ').trim().toLowerCase();
    if (/[^s]s$/.test(words)) return words;
  }
  return 'entries';
}
